import React from 'react';
import { motion } from 'framer-motion';
import { SettingsIcon, FlameIcon, DropletsIcon, PowerIcon, WrenchIcon, ActivityIcon, ShieldCheckIcon, ShieldAlertIcon, AlertTriangleIcon, BriefcaseIcon, TrendingUpIcon, PieChartIcon, MapIcon, PresentationIcon, UsersIcon, TargetIcon } from 'lucide-react';
import { ThreeBackground } from '../components/ThreeBackground';
import { Card3D } from '../components/Card3D';
import { PageTransition } from '../components/PageTransition';
export function ExperiencePage() {
  return <PageTransition data-id="element-148">
      {/* Page Header */}
      <section className="relative bg-navy-900 pt-32 pb-20 overflow-hidden bg-glow" data-id="element-149">
        <ThreeBackground data-id="element-150" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12" data-id="element-151">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-152">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-153">
              Career
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white" data-id="element-154">
              Executive Leadership Experience
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-light py-20" data-id="element-155">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-156">
          <div className="relative border-l-2 border-gold/30 pl-8 ml-4 md:ml-0 space-y-12" data-id="element-157">
            {/* Role 1 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-158">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-159" />
              <Card3D className="w-full" data-id="element-160">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-161">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-162">
                    2024 – Present
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-163">
                    Director, Field Operations – Gas Processing
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-164">
                    San Mateo Midstream (Matador Resources)
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-165">
                    Responsible for multi-site operations including five gas
                    processing trains totaling 720 MMSCFD capacity. Leads
                    capital projects, plant commissioning, and operational
                    strategy while advising executive leadership and board
                    stakeholders.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Role 2 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="relative" data-id="element-166">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-167" />
              <Card3D className="w-full" data-id="element-168">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-169">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-170">
                    2020 – 2024
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-171">
                    Plant Superintendent
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-172">
                    San Mateo Midstream (Matador Resources)
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-173">
                    Managed operations for three gas processing trains, ensuring
                    compliance with Process Safety Management standards while
                    improving plant uptime and operational efficiency.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Role 3 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative" data-id="element-174">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-175" />
              <Card3D className="w-full" data-id="element-176">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-177">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-178">
                    2017 – 2020
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-179">
                    Plant Operations Supervisor
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-180">
                    Targa Resources
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-181">
                    Oversaw operations of a 260 MMSCFD cryogenic gas plant while
                    supporting commercial, engineering, and executive leadership
                    teams to drive profitability and performance.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Role 4 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="relative" data-id="element-182">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-183" />
              <Card3D className="w-full" data-id="element-184">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-185">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-186">
                    2015 – 2017
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-187">
                    Assistant Plant Manager
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-188">
                    Boardwalk Field Services (Boardwalk Pipelines)
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-189">
                    Directed plant maintenance and operational teams, improving
                    reliability and reducing downtime while managing operational
                    budgets and safety programs.
                  </p>
                </div>
              </Card3D>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Expertise */}
      <section className="bg-navy-900 py-24" data-id="element-190">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-191">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-16" data-id="element-192">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-193">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-194">
              Core Expertise
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12" data-id="element-195">
            {/* Operations */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-196">
              <h3 className="text-xl font-semibold text-white/90 border-b border-white/10 pb-4 mb-6" data-id="element-197">
                Operations
              </h3>
              <ul className="space-y-4" data-id="element-198">
                <li className="flex items-center" data-id="element-199">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-200">
                    <SettingsIcon size={20} data-id="element-201" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-202">
                    Gas Processing Operations
                  </span>
                </li>
                <li className="flex items-center" data-id="element-203">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-204">
                    <FlameIcon size={20} data-id="element-205" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-206">
                    Cryogenic Plant Operations
                  </span>
                </li>
                <li className="flex items-center" data-id="element-207">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-208">
                    <DropletsIcon size={20} data-id="element-209" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-210">
                    Fractionation Systems
                  </span>
                </li>
                <li className="flex items-center" data-id="element-211">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-212">
                    <PowerIcon size={20} data-id="element-213" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-214">
                    Plant Commissioning
                  </span>
                </li>
                <li className="flex items-center" data-id="element-215">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-216">
                    <ActivityIcon size={20} data-id="element-217" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-218">
                    Start-Up &amp; Shutdown
                  </span>
                </li>
                <li className="flex items-center" data-id="element-219">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-220">
                    <WrenchIcon size={20} data-id="element-221" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-222">
                    Turnarounds &amp; Maintenance
                  </span>
                </li>
                <li className="flex items-center" data-id="element-223">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-224">
                    <TrendingUpIcon size={20} data-id="element-225" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-226">
                    Process Optimization
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* Safety & Compliance */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.15
          }} data-id="element-227">
              <h3 className="text-xl font-semibold text-white/90 border-b border-white/10 pb-4 mb-6" data-id="element-228">
                Safety &amp; Compliance
              </h3>
              <ul className="space-y-4" data-id="element-229">
                <li className="flex items-center" data-id="element-230">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-231">
                    <ShieldCheckIcon size={20} data-id="element-232" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-233">
                    Process Safety Management
                  </span>
                </li>
                <li className="flex items-center" data-id="element-234">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-235">
                    <ShieldAlertIcon size={20} data-id="element-236" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-237">
                    Safety Leadership
                  </span>
                </li>
                <li className="flex items-center" data-id="element-238">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-239">
                    <AlertTriangleIcon size={20} data-id="element-240" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-241">
                    Risk Management
                  </span>
                </li>
                <li className="flex items-center" data-id="element-242">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-243">
                    <ActivityIcon size={20} data-id="element-244" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-245">
                    Crisis Response
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* Leadership */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-246">
              <h3 className="text-xl font-semibold text-white/90 border-b border-white/10 pb-4 mb-6" data-id="element-247">
                Leadership
              </h3>
              <ul className="space-y-4" data-id="element-248">
                <li className="flex items-center" data-id="element-249">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-250">
                    <BriefcaseIcon size={20} data-id="element-251" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-252">
                    Capital Projects
                  </span>
                </li>
                <li className="flex items-center" data-id="element-253">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-254">
                    <TargetIcon size={20} data-id="element-255" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-256">
                    Operational Efficiency
                  </span>
                </li>
                <li className="flex items-center" data-id="element-257">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-258">
                    <PieChartIcon size={20} data-id="element-259" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-260">
                    Budget &amp; Cost Control
                  </span>
                </li>
                <li className="flex items-center" data-id="element-261">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-262">
                    <MapIcon size={20} data-id="element-263" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-264">
                    Multi-Site Management
                  </span>
                </li>
                <li className="flex items-center" data-id="element-265">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-266">
                    <PresentationIcon size={20} data-id="element-267" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-268">
                    Stakeholder Reporting
                  </span>
                </li>
                <li className="flex items-center" data-id="element-269">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-270">
                    <UsersIcon size={20} data-id="element-271" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-272">
                    Team Leadership
                  </span>
                </li>
                <li className="flex items-center" data-id="element-273">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-274">
                    <TargetIcon size={20} data-id="element-275" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-276">
                    Strategic Operations
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </PageTransition>;
}